<template>
  <div v-if="loaded" class="container py-5 pt-3 pt-xl-5">
    <button
      class="btn btn-outline-secondary mb-4"
      @click="goBackToRoleSelection"
    >
      <font-awesome-icon icon="arrow-left" class="me-2" />
      {{ $t("common.back") }}
    </button>

    <EESignupFlow v-if="activeFlow === 'EE'" />

    <LVSignupFlow v-if="activeFlow === 'LV'" />

    <SESignupFlow v-if="activeFlow === 'SE'" />

    <LTSignupFlow v-if="activeFlow === 'LT'" />

    <ContactContractSignupFlow
      v-if="activeFlow === 'ES' || activeFlow === 'PL'"
      :key="activeFlow"
      :country="activeFlow"
    />

    <!-- Showing the Finland Signup Flow is handled by the router, not a query search param -->

    <DefaultSignupFlow v-if="activeFlow === 'default'" />

    <!-- Simplified flow for non-admin producers in a supported, non-Estonian country -->
    <InternationalSignupFlow v-if="activeFlow === 'international'" />

    <NewCountrySignupFlow v-if="activeFlow === 'newCountry'" />

    <EmptySignupFlow v-if="activeFlow === 'empty'" />
  </div>
</template>

<script setup lang="ts">
import { useAccount } from "@/composables/account/useAccount";
import { useRouteCompany } from "@/composables/route/useCompany";
import { isAdminAccount } from "@/composables/useAdminCheck";
import {
  MAIN_SIGNUP_FLOW_COUNTRIES,
  SUPPORTED_COUNTRIES_WITH_LABELS,
} from "@/constants";
import ContactContractSignupFlow from "@/signup/common/ContactContractSignupFlow.vue";
import DefaultSignupFlow from "@/signup/default/DefaultSignupFlow.vue";
import EESignupFlow from "@/signup/ee/EESignupFlow.vue";
import EmptySignupFlow from "@/signup/EmptySignupFlowComponent.vue";
import InternationalSignupFlow from "@/signup/international/InternationalSignupFlow.vue";
import LTSignupFlow from "@/signup/lt/LTSignupFlow.vue";
import LVSignupFlow from "@/signup/lv/LVSignupFlow.vue";
import NewCountrySignupFlow from "@/signup/NewCountrySignupFlow.vue";
import SESignupFlow from "@/signup/se/SESignupFlow.vue";
import {
  getCountryFromCompanyOrLanguage,
  useSignupStore,
} from "@/signup/signup";
import { signupApi } from "@/signup/signupApi";
import { some } from "lodash-es";
import { storeToRefs } from "pinia";
import { computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

const router = useRouter();
const { globalErrorHandler } = useGlobalHandlers();
const signupStore = useSignupStore();
const { country: selectedCountry, account: accountData } =
  storeToRefs(signupStore);

type FlowKey =
  | "empty"
  | "newCountry"
  | "EE"
  | "international"
  | "LV"
  | "SE"
  | "LT"
  | "ES"
  | "PL"
  | "default";

// Single source of truth for flow selection. The flows are mutually exclusive, so the
// decision lives here rather than being smeared across overlapping per-block v-ifs.
//
// Non-admins in any supported, non-Estonian country get the simplified international flow
// (FI included — it intentionally loses its dedicated flow here). EE, admins, and
// unsupported/empty countries keep the existing per-country behavior.
const activeFlow = computed<FlowKey>(() => {
  const country = selectedCountry.value;
  if (!country) return "empty";
  if (!some(SUPPORTED_COUNTRIES_WITH_LABELS, { value: country })) {
    return "newCountry";
  }
  if (country === "EE") return "EE";
  if (!isAdminAccount(accountData.value)) return "international";
  return MAIN_SIGNUP_FLOW_COUNTRIES.includes(country)
    ? (country as FlowKey)
    : "default";
});

const companyId = useRouteCompany();
// The flows wait for this page's account: the signup store outlives the page, so on re-entry it
// still holds the previous visit's draft, which the flows would otherwise edit on mount.
const { loaded } = useAccount((fetchedAccountData) => {
  signupStore.initialize({
    initialAccount: fetchedAccountData,
    // Necessary so we can click "continue registration"
    defaultCountry: getCountryFromCompanyOrLanguage(
      fetchedAccountData,
      companyId.value
    ),
  });
});

const goBackToRoleSelection = () => {
  router.push("/signup");
};

onMounted(() => {
  signupApi
    .setOnboardingStep("PRODUCER")
    .catch((error) => globalErrorHandler(error));
});
</script>
